import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { Button } from '../../components/ui/button';
import {
  Shield, Users, DollarSign, Activity, HardDrive, LogOut,
  TrendingUp, Calendar, Settings, UserCog, Tag, RefreshCw,
  Eye, MousePointer, HeartPulse, CheckCircle, AlertTriangle, XCircle, Database, Lock, Briefcase, CreditCard
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [admin, setAdmin] = useState(null);
  const [stats, setStats] = useState(null);
  const [chartData, setChartData] = useState({});
  const [selectedRange, setSelectedRange] = useState('30d');
  const [selectedMetric, setSelectedMetric] = useState('users');
  const [pageAnalytics, setPageAnalytics] = useState(null);
  const [healthStatus, setHealthStatus] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('admin_token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/stats/overview`, {
        headers: getAuthHeaders()
      });
      setStats(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired');
        navigate('/admin/login');
      }
    }
  }, [getAuthHeaders, navigate]);

  const fetchPageAnalytics = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/analytics/page-visits`, {
        headers: getAuthHeaders(),
        params: { days: 30 }
      });
      setPageAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch page analytics:', error);
    }
  }, [getAuthHeaders]);

  const fetchHealthStatus = useCallback(async () => {
    setHealthLoading(true);
    try {
      const response = await axios.get(`${API}/admin/health`, {
        headers: getAuthHeaders()
      });
      setHealthStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch health status:', error);
      setHealthStatus({
        status: 'critical',
        message: 'Could not fetch health status',
        checks: {}
      });
    } finally {
      setHealthLoading(false);
    }
  }, [getAuthHeaders]);

  const fetchChartData = useCallback(async (metric, range) => {
    try {
      const response = await axios.get(`${API}/admin/stats/time-series`, {
        headers: getAuthHeaders(),
        params: { metric, range }
      });
      setChartData(prev => ({ ...prev, [metric]: response.data.data }));
    } catch (error) {
      console.error('Failed to fetch chart data:', error);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    const adminUser = localStorage.getItem('admin_user');
    
    if (!token) {
      navigate('/admin/login');
      return;
    }
    
    if (adminUser) {
      setAdmin(JSON.parse(adminUser));
    }
    
    const loadData = async () => {
      setLoading(true);
      await fetchStats();
      await fetchPageAnalytics();
      await fetchHealthStatus();
      await Promise.all([
        fetchChartData('users', selectedRange),
        fetchChartData('revenue', selectedRange),
        fetchChartData('active_users', selectedRange)
      ]);
      setLoading(false);
    };
    
    loadData();
  }, [navigate, fetchStats, fetchChartData, fetchPageAnalytics, fetchHealthStatus, selectedRange]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    navigate('/admin/login');
  };

  const handleRangeChange = async (range) => {
    setSelectedRange(range);
    await Promise.all([
      fetchChartData('users', range),
      fetchChartData('revenue', range),
      fetchChartData('active_users', range)
    ]);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(value);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <meta name="robots" content="noindex, nofollow" />
      
      <div className="min-h-screen bg-slate-900">
        {/* Header */}
        <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Admin Dashboard</h1>
                <p className="text-sm text-slate-400">Welcome, {admin?.name}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <nav className="flex gap-2">
                <Button
                  variant="ghost"
                  onClick={() => navigate('/admin/customers')}
                  className="text-slate-300 hover:text-white hover:bg-slate-700"
                >
                  <UserCog className="w-4 h-4 mr-2" />
                  Customers
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/admin/plans')}
                  className="text-slate-300 hover:text-white hover:bg-slate-700"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Plans
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/admin/stripe')}
                  className="text-purple-300 hover:text-white hover:bg-purple-700/50"
                >
                  <DollarSign className="w-4 h-4 mr-2" />
                  Stripe
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/admin/coupons')}
                  className="text-slate-300 hover:text-white hover:bg-slate-700"
                >
                  <Tag className="w-4 h-4 mr-2" />
                  Coupons
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/admin/settings')}
                  className="text-slate-300 hover:text-white hover:bg-slate-700"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </Button>
              </nav>
              
              <Button
                variant="ghost"
                onClick={handleLogout}
                className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </header>

        <main className="p-6">
          {/* System Health Status */}
          {healthStatus && (
            <div className={`mb-6 rounded-xl border p-4 ${
              healthStatus.status === 'healthy' 
                ? 'bg-emerald-500/10 border-emerald-500/30' 
                : healthStatus.status === 'warning'
                ? 'bg-amber-500/10 border-amber-500/30'
                : 'bg-red-500/10 border-red-500/30'
            }`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    healthStatus.status === 'healthy' 
                      ? 'bg-emerald-500/20' 
                      : healthStatus.status === 'warning'
                      ? 'bg-amber-500/20'
                      : 'bg-red-500/20'
                  }`}>
                    <HeartPulse className={`w-5 h-5 ${
                      healthStatus.status === 'healthy' 
                        ? 'text-emerald-400' 
                        : healthStatus.status === 'warning'
                        ? 'text-amber-400'
                        : 'text-red-400'
                    }`} />
                  </div>
                  <div>
                    <h3 className="font-bold text-white flex items-center gap-2">
                      System Health
                      {healthStatus.status === 'healthy' && <CheckCircle className="w-4 h-4 text-emerald-400" />}
                      {healthStatus.status === 'warning' && <AlertTriangle className="w-4 h-4 text-amber-400" />}
                      {healthStatus.status === 'critical' && <XCircle className="w-4 h-4 text-red-400" />}
                    </h3>
                    <p className="text-xs text-slate-400">
                      Last checked: {healthStatus.timestamp ? new Date(healthStatus.timestamp).toLocaleTimeString() : 'N/A'}
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={fetchHealthStatus}
                  disabled={healthLoading}
                  className="text-slate-400 hover:text-white"
                >
                  <RefreshCw className={`w-4 h-4 ${healthLoading ? 'animate-spin' : ''}`} />
                </Button>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {/* Database */}
                <div className="bg-slate-800/50 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Database className="w-4 h-4 text-slate-400" />
                    <span className="text-xs font-medium text-slate-300">Database</span>
                  </div>
                  <div className="flex items-center gap-1">
                    {healthStatus.checks?.database?.status === 'healthy' 
                      ? <CheckCircle className="w-3 h-3 text-emerald-400" />
                      : <XCircle className="w-3 h-3 text-red-400" />
                    }
                    <span className={`text-xs ${
                      healthStatus.checks?.database?.status === 'healthy' ? 'text-emerald-400' : 'text-red-400'
                    }`}>
                      {healthStatus.checks?.database?.status === 'healthy' ? 'Connected' : 'Error'}
                    </span>
                  </div>
                </div>
                
                {/* Security */}
                <div className="bg-slate-800/50 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Lock className="w-4 h-4 text-slate-400" />
                    <span className="text-xs font-medium text-slate-300">Security</span>
                  </div>
                  <div className="flex items-center gap-1">
                    {healthStatus.checks?.security?.status === 'healthy' 
                      ? <CheckCircle className="w-3 h-3 text-emerald-400" />
                      : healthStatus.checks?.security?.status === 'warning'
                      ? <AlertTriangle className="w-3 h-3 text-amber-400" />
                      : <XCircle className="w-3 h-3 text-red-400" />
                    }
                    <span className={`text-xs ${
                      healthStatus.checks?.security?.status === 'healthy' 
                        ? 'text-emerald-400' 
                        : healthStatus.checks?.security?.status === 'warning'
                        ? 'text-amber-400'
                        : 'text-red-400'
                    }`}>
                      {healthStatus.checks?.security?.failed_1h || 0}/hr
                    </span>
                  </div>
                </div>
                
                {/* Activity */}
                <div className="bg-slate-800/50 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Briefcase className="w-4 h-4 text-slate-400" />
                    <span className="text-xs font-medium text-slate-300">Activity</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <CheckCircle className="w-3 h-3 text-emerald-400" />
                    <span className="text-xs text-emerald-400">
                      {healthStatus.checks?.activity?.jobs_updated_24h || 0} jobs/24h
                    </span>
                  </div>
                </div>
                
                {/* Subscriptions */}
                <div className="bg-slate-800/50 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <CreditCard className="w-4 h-4 text-slate-400" />
                    <span className="text-xs font-medium text-slate-300">Subs</span>
                  </div>
                  <div className="flex items-center gap-1">
                    {healthStatus.checks?.subscriptions?.status === 'healthy' 
                      ? <CheckCircle className="w-3 h-3 text-emerald-400" />
                      : <AlertTriangle className="w-3 h-3 text-amber-400" />
                    }
                    <span className={`text-xs ${
                      healthStatus.checks?.subscriptions?.status === 'healthy' ? 'text-emerald-400' : 'text-amber-400'
                    }`}>
                      {healthStatus.checks?.subscriptions?.total_paid || 0} paid
                    </span>
                  </div>
                </div>
                
                {/* Storage */}
                <div className="bg-slate-800/50 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <HardDrive className="w-4 h-4 text-slate-400" />
                    <span className="text-xs font-medium text-slate-300">Data</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <CheckCircle className="w-3 h-3 text-emerald-400" />
                    <span className="text-xs text-emerald-400">
                      {healthStatus.checks?.storage?.total_jobs || 0} jobs
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Show issues if any */}
              {healthStatus.issues && healthStatus.issues.length > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-700">
                  <p className="text-xs font-medium text-amber-400 mb-1">Issues Detected:</p>
                  <ul className="text-xs text-slate-400 space-y-1">
                    {healthStatus.issues.map((issue, idx) => (
                      <li key={idx} className="flex items-center gap-2">
                        <AlertTriangle className="w-3 h-3 text-amber-400" />
                        {issue}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {/* Total Users */}
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-400" />
                </div>
                <span className="text-emerald-400 text-sm font-medium">
                  +{stats?.users?.new_last_7_days || 0} this week
                </span>
              </div>
              <p className="text-3xl font-bold text-white mb-1">{stats?.users?.total || 0}</p>
              <p className="text-slate-400 text-sm">Total Users</p>
            </div>

            {/* MRR */}
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-emerald-400" />
                </div>
                <span className="text-slate-400 text-sm">
                  ARR: {formatCurrency(stats?.revenue?.arr_estimate || 0)}
                </span>
              </div>
              <p className="text-3xl font-bold text-white mb-1">
                {formatCurrency(stats?.revenue?.mrr_total || 0)}
              </p>
              <p className="text-slate-400 text-sm">Monthly Recurring Revenue</p>
            </div>

            {/* Daily Active Users */}
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center">
                  <Activity className="w-6 h-6 text-purple-400" />
                </div>
              </div>
              <p className="text-3xl font-bold text-white mb-1">{stats?.users?.daily_active || 0}</p>
              <p className="text-slate-400 text-sm">Daily Active Users</p>
            </div>

            {/* Storage */}
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-amber-500/20 rounded-lg flex items-center justify-center">
                  <HardDrive className="w-6 h-6 text-amber-400" />
                </div>
              </div>
              <p className="text-3xl font-bold text-white mb-1">
                {(stats?.storage?.total_photos || 0) + (stats?.storage?.receipt_photos || 0)}
              </p>
              <p className="text-slate-400 text-sm">
                Photos ({stats?.storage?.total_photos || 0}) + Receipts ({stats?.storage?.receipt_photos || 0})
              </p>
            </div>
          </div>

          {/* Churn Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {/* Customer Churn Rate */}
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-red-500/20 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-red-400 rotate-180" />
                </div>
                <span className={`text-sm font-medium ${
                  (stats?.churn?.customer_churn_rate || 0) > 5 ? 'text-red-400' : 'text-emerald-400'
                }`}>
                  {stats?.churn?.customers_churned || 0} this month
                </span>
              </div>
              <p className="text-3xl font-bold text-white mb-1">
                {stats?.churn?.customer_churn_rate || 0}%
              </p>
              <p className="text-slate-400 text-sm">Customer Churn Rate</p>
            </div>

            {/* Revenue Churn Rate */}
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-orange-500/20 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-orange-400" />
                </div>
                <span className={`text-sm font-medium ${
                  (stats?.churn?.revenue_churn_rate || 0) > 5 ? 'text-red-400' : 'text-emerald-400'
                }`}>
                  -${stats?.churn?.mrr_lost || 0} MRR
                </span>
              </div>
              <p className="text-3xl font-bold text-white mb-1">
                {stats?.churn?.revenue_churn_rate || 0}%
              </p>
              <p className="text-slate-400 text-sm">Revenue Churn Rate</p>
            </div>

            {/* Paying Customers */}
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-emerald-400" />
                </div>
              </div>
              <p className="text-3xl font-bold text-white mb-1">
                {stats?.churn?.paying_customers || 0}
              </p>
              <p className="text-slate-400 text-sm">Paying Customers</p>
            </div>

            {/* Current MRR */}
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-blue-400" />
                </div>
              </div>
              <p className="text-3xl font-bold text-white mb-1">
                ${stats?.revenue?.mrr || 0}
              </p>
              <p className="text-slate-400 text-sm">Current MRR</p>
            </div>
          </div>

          {/* Subscription Breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
            {['free', 'solo', 'crew'].map(tier => (
              <div key={tier} className={`rounded-xl p-6 border ${
                tier === 'solo' ? 'bg-[#FF5F00]/10 border-[#FF5F00]/30' : 'bg-slate-800 border-slate-700'
              }`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-white capitalize">{tier === 'free' ? 'Side Hustle' : tier === 'solo' ? 'Solo Pro' : 'Crew'}</h3>
                  <span className={`text-2xl font-bold ${tier === 'solo' ? 'text-[#FF5F00]' : 'text-white'}`}>
                    {stats?.users?.by_tier?.[tier] || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-400">MRR</span>
                  <span className="text-white font-medium">
                    {formatCurrency(stats?.revenue?.mrr_by_tier?.[tier] || 0)}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Charts Section */}
          <div className="bg-slate-800 rounded-xl border border-slate-700 p-6 mb-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">Analytics</h2>
              <div className="flex gap-2">
                {['7d', '30d', '90d', '1y'].map(range => (
                  <Button
                    key={range}
                    variant={selectedRange === range ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => handleRangeChange(range)}
                    className={selectedRange === range 
                      ? 'bg-red-600 text-white' 
                      : 'text-slate-400 hover:text-white'
                    }
                  >
                    {range}
                  </Button>
                ))}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    fetchStats();
                    handleRangeChange(selectedRange);
                  }}
                  className="text-slate-400 hover:text-white ml-2"
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* User Growth Chart */}
              <div>
                <h3 className="text-sm font-medium text-slate-400 mb-4">User Growth</h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData.users || []}>
                      <defs>
                        <linearGradient id="userGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis 
                        dataKey="date" 
                        stroke="#64748B"
                        tick={{ fill: '#64748B', fontSize: 12 }}
                        tickFormatter={(val) => val.slice(5)}
                      />
                      <YAxis stroke="#64748B" tick={{ fill: '#64748B', fontSize: 12 }} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#1E293B', 
                          border: '1px solid #334155',
                          borderRadius: '8px'
                        }}
                        labelStyle={{ color: '#94A3B8' }}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="value" 
                        stroke="#3B82F6" 
                        fill="url(#userGradient)"
                        name="Users"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Revenue Chart */}
              <div>
                <h3 className="text-sm font-medium text-slate-400 mb-4">MRR Growth</h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData.revenue || []}>
                      <defs>
                        <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis 
                        dataKey="date" 
                        stroke="#64748B"
                        tick={{ fill: '#64748B', fontSize: 12 }}
                        tickFormatter={(val) => val.slice(5)}
                      />
                      <YAxis 
                        stroke="#64748B" 
                        tick={{ fill: '#64748B', fontSize: 12 }}
                        tickFormatter={(val) => `$${val}`}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#1E293B', 
                          border: '1px solid #334155',
                          borderRadius: '8px'
                        }}
                        labelStyle={{ color: '#94A3B8' }}
                        formatter={(value) => [`$${value}`, 'MRR']}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="value" 
                        stroke="#10B981" 
                        fill="url(#revenueGradient)"
                        name="MRR"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Daily Active Users Chart */}
              <div className="lg:col-span-2">
                <h3 className="text-sm font-medium text-slate-400 mb-4">Daily Active Users</h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData.active_users || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis 
                        dataKey="date" 
                        stroke="#64748B"
                        tick={{ fill: '#64748B', fontSize: 12 }}
                        tickFormatter={(val) => val.slice(5)}
                      />
                      <YAxis stroke="#64748B" tick={{ fill: '#64748B', fontSize: 12 }} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#1E293B', 
                          border: '1px solid #334155',
                          borderRadius: '8px'
                        }}
                        labelStyle={{ color: '#94A3B8' }}
                      />
                      <Bar dataKey="value" fill="#8B5CF6" radius={[4, 4, 0, 0]} name="Active Users" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>

          {/* Page Analytics Section */}
          {pageAnalytics && (
            <div className="bg-slate-800 rounded-xl border border-slate-700 p-6 mb-8">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
                    <Eye className="w-5 h-5 text-orange-400" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Page Visit Analytics</h2>
                    <p className="text-sm text-slate-400">Last {pageAnalytics.period_days} days</p>
                  </div>
                </div>
              </div>

              {/* Page Visit Stats Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                {['landing', 'pricing', 'register', 'login'].map(page => {
                  const pageData = pageAnalytics.totals?.[page] || { total: 0, avg_daily: 0 };
                  const pageLabels = {
                    landing: { name: 'Homepage', color: 'text-blue-400', bg: 'bg-blue-500/20' },
                    pricing: { name: 'Pricing', color: 'text-emerald-400', bg: 'bg-emerald-500/20' },
                    register: { name: 'Sign Up', color: 'text-orange-400', bg: 'bg-orange-500/20' },
                    login: { name: 'Login', color: 'text-purple-400', bg: 'bg-purple-500/20' }
                  };
                  const label = pageLabels[page];
                  
                  return (
                    <div key={page} className="bg-slate-700/50 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`w-8 h-8 ${label.bg} rounded-lg flex items-center justify-center`}>
                          <MousePointer className={`w-4 h-4 ${label.color}`} />
                        </div>
                        <span className="text-sm font-medium text-slate-300">{label.name}</span>
                      </div>
                      <p className={`text-2xl font-bold ${label.color}`}>{pageData.total.toLocaleString()}</p>
                      <p className="text-xs text-slate-500 mt-1">~{pageData.avg_daily} daily avg</p>
                    </div>
                  );
                })}
              </div>

              {/* Page Visits Chart */}
              {pageAnalytics.daily?.length > 0 && (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={pageAnalytics.daily}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis 
                        dataKey="date" 
                        stroke="#64748B"
                        tick={{ fill: '#64748B', fontSize: 10 }}
                        tickFormatter={(val) => val.slice(5)}
                      />
                      <YAxis stroke="#64748B" tick={{ fill: '#64748B', fontSize: 12 }} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#1E293B', 
                          border: '1px solid #334155',
                          borderRadius: '8px'
                        }}
                        labelStyle={{ color: '#94A3B8' }}
                      />
                      <Legend />
                      <Bar dataKey="landing" name="Homepage" fill="#3B82F6" radius={[2, 2, 0, 0]} stackId="a" />
                      <Bar dataKey="pricing" name="Pricing" fill="#10B981" radius={[2, 2, 0, 0]} stackId="a" />
                      <Bar dataKey="register" name="Sign Up" fill="#F97316" radius={[2, 2, 0, 0]} stackId="a" />
                      <Bar dataKey="login" name="Login" fill="#8B5CF6" radius={[2, 2, 0, 0]} stackId="a" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          )}

          {/* Activity Summary */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-bold text-white mb-4">Activity Summary</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Total Jobs Created</span>
                  <span className="text-xl font-bold text-white">{stats?.activity?.total_jobs || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Total Expenses Logged</span>
                  <span className="text-xl font-bold text-white">{stats?.activity?.total_expenses || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Custom Pricing Overrides</span>
                  <span className="text-xl font-bold text-white">{stats?.users?.custom_pricing || 0}</span>
                </div>
              </div>
            </div>

            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-bold text-white mb-4">Quick Actions</h3>
              <div className="grid grid-cols-2 gap-3">
                <Button 
                  onClick={() => navigate('/admin/customers')}
                  className="h-12 bg-slate-700 hover:bg-slate-600 text-white"
                >
                  <Users className="w-4 h-4 mr-2" />
                  View Customers
                </Button>
                <Button 
                  onClick={() => navigate('/admin/plans')}
                  className="h-12 bg-slate-700 hover:bg-slate-600 text-white"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Manage Plans
                </Button>
                <Button 
                  onClick={() => navigate('/admin/coupons')}
                  className="h-12 bg-slate-700 hover:bg-slate-600 text-white"
                >
                  <Tag className="w-4 h-4 mr-2" />
                  Create Coupon
                </Button>
                <Button 
                  onClick={() => navigate('/admin/settings')}
                  className="h-12 bg-slate-700 hover:bg-slate-600 text-white"
                >
                  <UserCog className="w-4 h-4 mr-2" />
                  Admin Settings
                </Button>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}
